import { computed, watchEffect } from "vue";
import { useRouter, type RouteLocationRaw } from "vue-router";
import { useI18n } from "vue-i18n";
import { useAbility } from "@/api/models/auth/ability";
import { useCompanyMePaginated } from "@/composables/models/company/useCompanyMe";
import { usePracticeMePaginated } from "@/composables/models/practice/usePracticeMe";
import { useMyMsos } from "@/composables/models/practice/useMsos";
import { useCanAccessOfficesManagement } from "@/composables/shared/useSectionPermissions";

export const ORGANIZATION_LEVELS = ["dso", "practices", "offices"] as const;
export type TOrganizationLevel = (typeof ORGANIZATION_LEVELS)[number];
export const ORGANIZATION_ROOT_PATH = "/organization";
export const ORGANIZATION_LEVEL_PATHS: Record<TOrganizationLevel, string> = {
  dso: "/organization/dso",
  practices: "/organization/practices",
  offices: "/organization/offices",
};
export const ORGANIZATION_DETAIL_PREFIXES = ["/msos", "/companies", "/practices"];
export const DSO_REPRESENTATIVE_PATH = "/organization/dso-representative";
export const DSO_TEAM_PATH = "/organization/team";

export const isDsoRepresentativePath = (path: string) =>
  path.startsWith("/msos/") && path.endsWith("/representative");

export const isDsoTeamPath = (path: string) => path.startsWith("/msos/") && path.endsWith("/team");

const detailRoute = (level: TOrganizationLevel, id: string): RouteLocationRaw => {
  switch (level) {
    case "dso":
      return { name: "/msos/[id]/", params: { id } };
    case "practices":
      return { name: "/companies/[id].[[tab]]", params: { id }, query: { scope: "me" } };
    case "offices":
      return { name: "/practices/[id].[[tab]]", params: { id }, query: { scope: "me" } };
  }
};

export function useMyOrganization() {
  const { can } = useAbility();
  const canAccess = useCanAccessOfficesManagement();
  const canReadMsos = computed(() => can($permActions.READ, $permSubjects.PRACTICE__MSO));

  const { msos, isLoading: isMsosLoading, query: msosQuery } = useMyMsos();
  const companiesQuery = useCompanyMePaginated({ enabled: canAccess });
  const practicesQuery = usePracticeMePaginated({ enabled: canAccess });

  const companies = computed(
    () => companiesQuery.data.value?.pages.flatMap((page) => page.results) ?? []
  );
  const practices = computed(
    () => practicesQuery.data.value?.pages.flatMap((page) => page.results) ?? []
  );

  const idsByLevel = computed<Record<TOrganizationLevel, string[]>>(() => ({
    dso: msos.value.map((mso) => mso.id),
    practices: companies.value.map((company) => company.id),
    offices: practices.value.map((practice) => practice.id),
  }));

  const isLoading = computed(
    () => isMsosLoading.value || companiesQuery.isLoading.value || practicesQuery.isLoading.value
  );

  const isResolved = computed(
    () =>
      (!canReadMsos.value || msosQuery.isFetched.value) &&
      (!canAccess.value || (companiesQuery.isFetched.value && practicesQuery.isFetched.value))
  );

  const hasLevel = (level: TOrganizationLevel) => idsByLevel.value[level].length > 0;

  const routeForLevel = (level: TOrganizationLevel): RouteLocationRaw | undefined => {
    const ids = idsByLevel.value[level];
    const [onlyId] = ids;

    if (ids.length === 1 && onlyId) return detailRoute(level, onlyId);
    if (ids.length > 1) return ORGANIZATION_LEVEL_PATHS[level];

    const fallbackLevel = ORGANIZATION_LEVELS.slice(ORGANIZATION_LEVELS.indexOf(level) + 1).find(
      (nextLevel) => hasLevel(nextLevel)
    );

    return fallbackLevel ? routeForLevel(fallbackLevel) : undefined;
  };

  return {
    msos,
    companies,
    practices,
    msosQuery,
    companiesQuery,
    practicesQuery,
    idsByLevel,
    isLoading,
    isResolved,
    hasLevel,
    routeForLevel,
  };
}

export function useOrganizationLevel(level: TOrganizationLevel) {
  const router = useRouter();
  const organization = useMyOrganization();

  const isList = computed(() => organization.idsByLevel.value[level].length > 1);
  const forwardTo = computed(() => (isList.value ? undefined : organization.routeForLevel(level)));
  const isEmpty = computed(
    () => organization.isResolved.value && !isList.value && !forwardTo.value
  );

  watchEffect(() => {
    if (!organization.isResolved.value || !forwardTo.value) return;

    router.replace(forwardTo.value);
  });

  return { ...organization, isList, isEmpty };
}

type TDsoSectionRoute = "/msos/[id]/representative" | "/msos/[id]/team";

/** A DSO-level section that forwards straight to the only DSO, or lists them when there are several. */
function useDsoSection(routeName: TDsoSectionRoute) {
  const router = useRouter();
  const { msos, msosQuery, isResolved } = useMyOrganization();

  const isList = computed(() => msos.value.length > 1);
  const onlyMso = computed(() => (msos.value.length === 1 ? msos.value[0] : undefined));
  const isEmpty = computed(() => isResolved.value && msos.value.length === 0);

  watchEffect(() => {
    if (!isResolved.value || !onlyMso.value) return;

    router.replace({
      name: routeName,
      params: { id: onlyMso.value.id },
    });
  });

  return { msos, msosQuery, isList, isEmpty };
}

/**
 * Which organization the user is acting inside, shown before a section's eyebrow ("DSO PRACTICES").
 * Undefined when they belong to no DSO, so the eyebrow stays unprefixed rather than claiming a mode.
 */
export function useOrganizationMode() {
  const { t } = useI18n();
  const { hasLevel } = useMyOrganization();
  return computed(() => (hasLevel("dso") ? t("generic.entity.dso") : undefined));
}

export function useDsoRepresentative() {
  return useDsoSection("/msos/[id]/representative");
}

export function useDsoTeam() {
  return useDsoSection("/msos/[id]/team");
}
